'use client';

import React, { useEffect, useState } from 'react';
import { GoCheckCircleFill  } from 'react-icons/go';
import { PiSealWarningFill, PiWarningOctagonFill, PiWarningCircleFill  } from 'react-icons/pi';
import Swal from 'sweetalert2'
import DOMPurify from 'dompurify';

import { alertService, alertType } from '../_services/alert.service';

interface AlertButton {
    text: string;
    onClick: () => void;
    className?: string;
}

interface AlertProps {
    message?: string;
    type: typeof alertType[keyof typeof alertType];
    autoClose?: boolean;
    msgtimeout?: number;
    fade?: boolean;
    isShowSwal?: boolean;
    showButton?: boolean;
    button?: AlertButton;
}

export default function Alerts(){
    const [alerts, setAlerts] = useState<AlertProps[]>([]);

    useEffect(() => {
        const subscription = alertService.onAlert().subscribe((alert: unknown) => {
            if (!(alert as AlertProps).message) {
                return;
            }

            if ((alert as AlertProps).isShowSwal) {
                const alertData = alert as AlertProps;
                Swal.fire({
                    icon: (['success', 'error', 'warning', 'info', 'question'].includes(alertData.type) ? alertData.type as 'success' | 'error' | 'warning' | 'info' | 'question' : undefined),
                    title: alertData.type.charAt(0).toUpperCase() + alertData.type.slice(1),
                    html: DOMPurify.sanitize(alertData.message ?? ''),
                    showCloseButton: false,
                    showConfirmButton: alertData.showButton || false,
                    confirmButtonText: alertData.button?.text || 'OK',
                    allowOutsideClick: true,
                    timer: alertData.msgtimeout === 0 ? undefined : (alertData.msgtimeout ?? 3500),
                    customClass: {
                      popup: 'swal-popup',
                      confirmButton: 'swal-button',
                    },
                }).then((result) => {
                    if (result.isConfirmed && alertData.button?.onClick) {
                        alertData.button.onClick();
                    }
                });

                return;
            }

            //only pushes one message every time shows message. but can modify it to keep showing messages
            setAlerts([(alert as AlertProps)]);
    
            // auto close alert in 2.5secs
            if ((alert as AlertProps).autoClose) {
                const istimeoutavble = ((alert as AlertProps).msgtimeout ? (alert as AlertProps).msgtimeout : 3500);
                setTimeout(() => {
                    removeAlert();
                }, istimeoutavble);
            }
        });
      
        function unsub(){
            subscription.unsubscribe();
        }
      
        return unsub;
    });

    //delete alert by clicking
    const removeAlert = () => {
        const selelem = document.querySelector(".alert-popup");
        if(selelem){
            selelem.classList.add("alert-dismissable");
        }

        setTimeout(() => {
            setAlerts([]);
        }, 500);
    }

    const handleButtonClick = (alert: AlertProps) => {
        if (alert.button?.onClick) {
            alert.button.onClick();
        }
        removeAlert();
    }

    //css classes for each message containing alerttypes
    const cssClasses = (alert: AlertProps) => {
        if (!alert) return;

        const classes = ['alert-popup'];

        const alertTypeClass = {
            [alertType.success]: 'alert-popup alert-success',
            [alertType.error]: 'alert-popup alert-danger',
            [alertType.info]: 'alert-popup alert-info',
            [alertType.warning]: 'alert-popup alert-warning'
        }

        classes.push(alertTypeClass[alert.type]);

        if (alert.fade) {
            classes.push('fade');
        }

        return classes.join(' ');
    }

    return (<>
        {alerts.length > 0 && <div className="alert-container">
            {alerts.map((alert, index) =>
                <div key={index} className={cssClasses(alert)}>
                    <div className="alert-icon">
                        {alert.type === alertType.success && <GoCheckCircleFill />}
                        {alert.type === alertType.error && <PiWarningOctagonFill />}
                        {alert.type === alertType.info && <PiSealWarningFill />}
                        {alert.type === alertType.warning && <PiWarningCircleFill />}
                    </div>
                    <label className="close" id={`alert-close-${index}`} onClick={() => removeAlert()}>&times;</label>
                    <span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(alert.message ?? '')}}></span>
                    
                    {alert.showButton && alert.button && (
                        <button 
                            className={`alert-button`}
                            onClick={() => handleButtonClick(alert)}
                        >
                            {alert.button.text}
                        </button>
                    )}
                </div>
            )}
        </div>}
    </>)
}

